import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@heroui/react';
import type { JournalLine } from '@/api/models';
import { formatGel } from '@/pages/accounting/books/chartOfAccounts/utils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { AccountLabel } from './useAccountLabelMap';

type JournalEntryLinesProps = {
    lines: JournalLine[];
    accountMap: Map<string, AccountLabel>;
    totalDebitTetri: number;
    totalCreditTetri: number;
    /** testid discriminator — `journal-lines-${slot}-…`. */
    slot: string;
};

/**
 * The canonical presentation of a journal entry's lines: account code + name,
 * a DEBIT and a CREDIT column, and a totals row.
 *
 * Extracted from `JournalEntryDrawer` so the year-end close and rollover
 * previews show a not-yet-posted entry in exactly the same shape the journal
 * shows a posted one — the user compares the preview to the audit trail, so the
 * two must not drift.
 *
 * The OSS `Table` compound: a short, static list inside an overlay, where a
 * virtualized register grid would be weight without benefit. The totals are the
 * last row of the body rather than a `Table.Footer` — the footer slot renders
 * outside the table, and the totals must stay column-aligned with the lines
 * above them and be read as part of the same listing.
 */
export const JournalEntryLines: React.FC<JournalEntryLinesProps> = ({
    lines,
    accountMap,
    totalDebitTetri,
    totalCreditTetri,
    slot,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div {...getNodeTestIdProp(`journal-lines-${slot}`)}>
            <Table>
                <Table.ScrollContainer>
                    <Table.Content
                        aria-label={t('journal.drawer.lines')}
                        className="min-w-[22rem] text-sm"
                    >
                        <Table.Header>
                            <Table.Column id="account" isRowHeader>
                                {t('journal.drawer.account')}
                            </Table.Column>
                            <Table.Column id="debit">
                                {t('journal.drawer.debit')}
                            </Table.Column>
                            <Table.Column id="credit">
                                {t('journal.drawer.credit')}
                            </Table.Column>
                        </Table.Header>
                        <Table.Body>
                            {lines.map((line, index) => {
                                const account = accountMap.get(line.accountId);
                                return (
                                    <Table.Row
                                        id={`${line.accountId}-${index}`}
                                        key={`${line.accountId}-${index}`}
                                    >
                                        <Table.Cell>
                                            <span className="flex min-w-0 items-center gap-2">
                                                <span className="shrink-0 text-xs tabular-nums text-muted">
                                                    {account?.code ?? ''}
                                                </span>
                                                <span className="truncate text-foreground">
                                                    {account?.name ??
                                                        line.accountId}
                                                </span>
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="block whitespace-nowrap text-right tabular-nums text-foreground">
                                                {line.debitTetri > 0
                                                    ? formatGel(line.debitTetri)
                                                    : '–'}
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="block whitespace-nowrap text-right tabular-nums text-foreground">
                                                {line.creditTetri > 0
                                                    ? formatGel(
                                                          line.creditTetri,
                                                      )
                                                    : '–'}
                                            </span>
                                        </Table.Cell>
                                    </Table.Row>
                                );
                            })}
                            <Table.Row
                                className="bg-surface-secondary font-semibold"
                                id={`journal-lines-totals-${slot}`}
                                key={`journal-lines-totals-${slot}`}
                            >
                                <Table.Cell>
                                    {t('journal.drawer.totals')}
                                </Table.Cell>
                                <Table.Cell
                                    {...getNodeTestIdProp(
                                        `journal-lines-total-debit-${slot}`,
                                    )}
                                >
                                    <span className="block whitespace-nowrap text-right tabular-nums">
                                        {formatGel(totalDebitTetri)}
                                    </span>
                                </Table.Cell>
                                <Table.Cell
                                    {...getNodeTestIdProp(
                                        `journal-lines-total-credit-${slot}`,
                                    )}
                                >
                                    <span className="block whitespace-nowrap text-right tabular-nums">
                                        {formatGel(totalCreditTetri)}
                                    </span>
                                </Table.Cell>
                            </Table.Row>
                        </Table.Body>
                    </Table.Content>
                </Table.ScrollContainer>
            </Table>
        </div>
    );
};
